import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDate } from "@/utils/formatUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Button, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteClientAgreement, useFilterClientAgreements } from "../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../common/constants";
import { ClientAgreement } from "../../types";
import { ClientAgreementModalForm } from "../forms/ClientAgreementModalForm";

type Props = {
  clientId: string;
  showActions?: boolean;
};

export const ClientAgreementsTable = ({ clientId, showActions }: Props) => {
  const { t } = useTranslation();

  const [pageIndex, setPageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agreementToUpdate, setAgreementToUpdate] = useState<ClientAgreement>();

  const { data } = useFilterClientAgreements({ id: clientId, object: { pageSize: PageSizes.LARGE, pageIndex } });
  const deleteAgreement = useDeleteClientAgreement();

  const columns: ColumnsType<ClientAgreement> = useMemo(() => {
    return [
      {
        key: "type",
        title: t("client.agreement.enums.clientAgreementType._label"),
        width: 700,
        render: (_, record) => t(`client.agreement.enums.clientAgreementType.${record.type}`),
      },
      {
        key: "date",
        title: t("client.agreement.attrs.date"),
        width: 190,
        render: (_, record) => formatLocaleDate(record.date),
      },
      {
        key: "active",
        title: t("client.agreement.attrs.active"),
        align: "center",
        width: 80,
        render: (_, record) =>
          record.active ? (
            <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
          ) : (
            <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
          ),
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon
              icon="edit"
              color="blue"
              text={t("common.edit")}
              onClick={() => {
                setIsModalOpen(true);
                setAgreementToUpdate(record);
              }}
            />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("client.agreement.helpers.deleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              cancelText={t("common.no")}
              okText={t("common.yes")}
              okType="danger"
              onConfirm={() => deleteAgreement.mutate({ id1: clientId, id2: record.id })}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
      },
    ];
  }, []);

  return (
    <>
      <Table<ClientAgreement>
        {...tableStandardProps()}
        scroll={{ x: TableSizes.LARGE }}
        columns={columns}
        dataSource={data?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => setPageIndex(pageIndex - 1),
        }}
      />

      {showActions && (
        <>
          <div className={data?.pageData.length ? "table-footer-margin" : "margin-top-small"}>
            <Button type="primary" size="small" icon={<AntIcon type="plus" />} onClick={() => setIsModalOpen(true)}>
              {t("common.add")}
            </Button>
          </div>

          <ClientAgreementModalForm
            isOpen={isModalOpen}
            clientId={clientId}
            agreement={agreementToUpdate}
            onCancel={() => {
              setIsModalOpen(false);
              setAgreementToUpdate(undefined);
            }}
          />
        </>
      )}
    </>
  );
};
